import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const services = [
  {
    id: 'system',
    icon: 'ri-server-line',
    title: 'システム開発',
    subtitle: 'System Development',
    color: 'from-teal-400 to-teal-600',
    bg: 'bg-teal-50',
    borderActive: 'border-teal-400',
    description:
      '業務効率化・DX推進に向けた各種システム・ソフトウェアの企画・設計・開発・配信まで一貫してサポートします。お客様の業務フローを深く理解し、最適なシステムを構築します。',
    features: [
      { icon: 'ri-file-list-3-line', title: '要件定義・設計', desc: 'お客様の課題を整理し、最適なシステム設計を行います。' },
      { icon: 'ri-code-s-slash-line', title: 'バックエンド開発', desc: 'スケーラブルで安全なサーバーサイドシステムを構築します。' },
      { icon: 'ri-cloud-line', title: 'クラウド構築', desc: 'AWS・GCP・Azureなど主要クラウドに対応しています。' },
      { icon: 'ri-tools-line', title: '保守・運用', desc: 'リリース後も継続的なサポートと改善を提供します。' },
    ],
    image: 'https://readdy.ai/api/search-image?query=abstract%20digital%20system%20network%20server%20infrastructure%20technology%20turquoise%20teal%20glowing%20circuit%20board%20data%20flow%20visualization%20modern%20IT%20software%20development%20clean%20minimal%20background%20with%20geometric%20shapes%20and%20light%20effects&width=800&height=500&seq=ocean-service-system-01&orientation=landscape',
  },
  {
    id: 'app',
    icon: 'ri-smartphone-line',
    title: 'アプリ開発',
    subtitle: 'App Development',
    color: 'from-cyan-400 to-cyan-600',
    bg: 'bg-cyan-50',
    borderActive: 'border-cyan-400',
    description:
      'iOS・Android対応のネイティブアプリからクロスプラットフォームアプリまで、ユーザー体験を重視した開発を行います。企画段階からリリース・運用まで一貫してサポートします。',
    features: [
      { icon: 'ri-apple-line', title: 'iOS / Android対応', desc: 'ネイティブ・クロスプラットフォーム両方に対応します。' },
      { icon: 'ri-palette-line', title: 'UI/UXデザイン', desc: '使いやすく美しいインターフェースを設計します。' },
      { icon: 'ri-links-line', title: 'API連携', desc: '外部サービスとのシームレスな連携を実現します。' },
      { icon: 'ri-store-2-line', title: 'ストア申請サポート', desc: 'App Store・Google Playへの申請をサポートします。' },
    ],
    image: 'https://readdy.ai/api/search-image?query=abstract%20mobile%20app%20development%20smartphone%20interface%20design%20turquoise%20cyan%20ocean%20wave%20inspired%20digital%20technology%20floating%20UI%20elements%20modern%20clean%20background%20with%20soft%20gradient%20and%20geometric%20shapes&width=800&height=500&seq=ocean-service-app-01&orientation=landscape',
  },
  {
    id: 'web',
    icon: 'ri-global-line',
    title: 'Webサービス運営',
    subtitle: 'Web Service',
    color: 'from-emerald-400 to-teal-500',
    bg: 'bg-emerald-50',
    borderActive: 'border-emerald-400',
    description:
      'Webサービスの企画・開発から運営・グロースハックまで。インターネット広告・広告代理業も対応しており、集客から収益化まで総合的にサポートします。',
    features: [
      { icon: 'ri-layout-line', title: 'Webサービス開発', desc: 'スケーラブルなWebアプリケーションを開発します。' },
      { icon: 'ri-advertisement-line', title: 'インターネット広告', desc: 'Google・SNS広告の運用・最適化を行います。' },
      { icon: 'ri-building-2-line', title: '広告代理業', desc: '広告戦略の立案から実行まで代理で対応します。' },
      { icon: 'ri-line-chart-line', title: 'SEO・グロース支援', desc: '検索流入増加とサービス成長を継続的に支援します。' },
    ],
    image: 'https://readdy.ai/api/search-image?query=abstract%20web%20service%20internet%20digital%20marketing%20website%20development%20emerald%20teal%20ocean%20inspired%20flowing%20data%20streams%20modern%20technology%20background%20with%20clean%20minimal%20design%20and%20glowing%20elements&width=800&height=500&seq=ocean-service-web-01&orientation=landscape',
  },
];

const flow = [
  { step: '01', title: 'ヒアリング', desc: 'お客様の課題・要望を丁寧にお聞きします。' },
  { step: '02', title: '提案・見積もり', desc: '最適なソリューションと費用をご提案します。' },
  { step: '03', title: '開発・制作', desc: '設計・開発・テストを丁寧に進めます。' },
  { step: '04', title: 'リリース・運用', desc: 'リリース後も継続的にサポートします。' },
];

export default function ServicesPage() {
  const [activeTab, setActiveTab] = useState('system');
  const activeService = services.find((s) => s.id === activeTab)!;

  useEffect(() => {
    const syncActiveTab = () => {
      const hash = window.location.hash.replace('#', '');

      if (services.some((service) => service.id === hash)) {
        setActiveTab(hash);

        const section = document.getElementById('services-tabs');
        if (section) {
          section.scrollIntoView({ behavior: 'auto', block: 'start' });
          window.scrollBy(0, -96);
        }
        return;
      }

      setActiveTab('system');
    };

    syncActiveTab();
    window.addEventListener('hashchange', syncActiveTab);

    return () => window.removeEventListener('hashchange', syncActiveTab);
  }, []);

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    window.location.hash = tabId;
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Page Hero */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-teal-900 to-cyan-800 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://readdy.ai/api/search-image?query=abstract%20turquoise%20ocean%20wave%20pattern%20digital%20technology%20background%20minimal%20clean%20teal%20cyan%20gradient%20with%20geometric%20shapes&width=1920&height=400&seq=ocean-services-hero-01&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 text-center">
          <p className="text-teal-200 text-xs font-semibold uppercase tracking-widest mb-3">Our Services</p>
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            サービス
          </h1>
          <p className="text-white/70 text-sm max-w-xl mx-auto">
            システム開発・アプリ開発・Webサービス運営まで、<br className="hidden md:block" />
            お客様のビジネス課題に合わせた最適なソリューションを提供します。
          </p>
        </div>
      </section>

      {/* Service Tabs */}
      <section id="services-tabs" className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          {/* Tab Buttons */}
          <div className="flex flex-wrap gap-3 justify-center mb-12">
            {services.map((s) => (
              <button
                key={s.id}
                onClick={() => handleTabChange(s.id)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full border text-sm font-medium cursor-pointer transition-all whitespace-nowrap ${
                  activeTab === s.id
                    ? `${s.borderActive} bg-teal-50 text-teal-700`
                    : 'border-gray-200 text-gray-600 hover:border-teal-300 hover:text-teal-600'
                }`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${s.icon} text-sm`}></i>
                </div>
                {s.title}
              </button>
            ))}
          </div>

          {/* Active Service Detail */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className={`w-14 h-14 flex items-center justify-center rounded-2xl bg-gradient-to-br ${activeService.color} mb-5`}>
                <i className={`${activeService.icon} text-white text-2xl`}></i>
              </div>
              <p className="text-teal-500 text-xs font-medium mb-1">{activeService.subtitle}</p>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                {activeService.title}
              </h2>
              <p className="text-gray-600 text-sm leading-relaxed mb-6">{activeService.description}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {activeService.features.map((f) => (
                  <div key={f.title} className={`${activeService.bg} rounded-xl p-4`}>
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white mb-2">
                      <i className={`${f.icon} text-teal-600 text-sm`}></i>
                    </div>
                    <h4 className="text-sm font-semibold text-gray-800 mb-1">{f.title}</h4>
                    <p className="text-xs text-gray-500 leading-relaxed">{f.desc}</p>
                  </div>
                ))}
              </div>
              <div className="mt-6">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-teal-400 to-cyan-500 text-white text-sm font-semibold cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap"
                >
                  このサービスについて相談する
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-sm"></i>
                  </div>
                </Link>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden">
              <img
                src={activeService.image}
                alt={activeService.title}
                className="w-full h-72 md:h-96 object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Flow */}
      <section className="py-16 md:py-20 bg-gray-50/50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10">
            <p className="text-teal-500 text-xs font-semibold uppercase tracking-widest mb-2">Process</p>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              ご依頼の流れ
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {flow.map((item, idx) => (
              <div key={item.step} className="relative bg-white rounded-2xl p-6 border border-gray-100">
                {idx < flow.length - 1 && (
                  <div className="hidden lg:block absolute top-8 -right-3 w-6 h-6 flex items-center justify-center z-10">
                    <i className="ri-arrow-right-line text-teal-300 text-lg"></i>
                  </div>
                )}
                <div className="text-3xl font-bold text-teal-100 mb-3" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  {item.step}
                </div>
                <h3 className="text-sm font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
